<template>
  <div class="customers">
    <div class="customers__content">
      <v-toolbar flat color="transparent" class="customers__toolbar">
        <v-toolbar-title>{{ $t("customers") }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="#403838"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="openCreateDialog"
        >
          {{ $t("add_customer") }}
        </v-btn>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :label="$t('search_this_page')"
          :hint="$t('search_this_page_hint')"
          single-line
          hide-details
          variant="outlined"
        ></v-text-field>
      </v-toolbar>

      <!-- Phone-contacts-style list: a row per customer, tap anywhere on
      it to edit - no separate edit icon, no table chrome that doesn't
      fit a phone width. -->
      <template v-if="isNarrow">
        <v-list v-if="filteredCustomers.length" lines="two" class="customers__list">
          <v-list-item
            v-for="customer in filteredCustomers"
            :key="customer.id"
            :title="customer.name"
            :subtitle="customer.phone_number || customer.email || '—'"
            prepend-icon="mdi-account-circle"
            @click="editItem(customer)"
          >
            <template #append>
              <span class="customers__list-visits">{{ customer.visit_count }}</span>
            </template>
          </v-list-item>
        </v-list>
        <div v-else class="customers__empty">
          <VIcon icon="mdi-account-group-outline" size="28" />
          <p>{{ $t("no_customers") }}</p>
        </div>

        <div class="customers__pagination">
          <v-pagination
            v-if="totalPages > 1"
            :model-value="page"
            :length="totalPages"
            density="comfortable"
            @update:model-value="goToPage"
          />
        </div>
      </template>

      <v-data-table-server
        v-else
        :headers="headers"
        :items="customers"
        :search="search"
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page-options="perPageChoices"
        :items-per-page-text="$t('records_on_page')"
        class="elevation-1 customers__table"
        @update:options="updatedOptions"
        @click:row="(_event: Event, { item }: { item: CompanyCustomer }) => editItem(item)"
      >
        <template v-slot:item.last_visit="{ item }">
          {{ item.last_visit ? humanizeDate(item.last_visit) : "—" }}
        </template>
        <template v-slot:no-data>
          <div class="customers__empty">
            <VIcon icon="mdi-account-group-outline" size="28" />
            <p>{{ $t("no_customers") }}</p>
          </div>
        </template>
      </v-data-table-server>
    </div>

    <v-dialog
      v-model="dialog"
      width="480"
      :fullscreen="mobile"
      eager
      @update:model-value="(open: boolean) => !open && (confirmingDelete = false)"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ $t("edit_customer") }}</span>
        </v-card-title>
        <v-card-text v-if="!confirmingDelete">
          <v-text-field
            v-model="editedCustomer.name"
            :label="$t('fio')"
            density="comfortable"
          />
          <v-text-field
            v-model="editedCustomer.phone_number"
            :label="$t('phone_number')"
            density="comfortable"
          />
          <v-text-field
            v-model="editedCustomer.email"
            :label="$t('email')"
            density="comfortable"
          />
          <v-text-field
            v-model="editedCustomer.address"
            :label="$t('address')"
            density="comfortable"
          />
          <v-alert v-if="saveError" type="error" density="compact" class="mt-2">
            {{ saveError }}
          </v-alert>
        </v-card-text>
        <v-card-text v-else>
          <v-alert type="warning" density="compact">
            {{ $t("delete_customer_confirm", { name: editedCustomer.name }) }}
          </v-alert>
          <v-alert v-if="deleteError" type="error" density="compact" class="mt-2">
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions v-if="!confirmingDelete">
          <v-btn variant="text" color="error" @click="confirmingDelete = true">
            {{ $t("delete") }}
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">{{ $t("cancel") }}</v-btn>
          <v-btn
            color="#403838"
            variant="flat"
            :loading="saving"
            @click="saveCustomer"
          >
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-spacer />
          <v-btn variant="text" @click="confirmingDelete = false">{{ $t("cancel") }}</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="deleteCustomer">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createDialog" width="480" :fullscreen="mobile" eager>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ $t("add_customer") }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newCustomer.name"
            :label="$t('fio')"
            density="comfortable"
          />
          <v-text-field
            v-model="newCustomer.phone_number"
            :label="$t('phone_number')"
            density="comfortable"
          />
          <v-text-field
            v-model="newCustomer.email"
            :label="$t('email')"
            density="comfortable"
          />
          <v-alert
            v-if="createError"
            type="error"
            density="compact"
            class="mt-2"
          >
            {{ createError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">{{
            $t("cancel")
          }}</v-btn>
          <v-btn
            color="#403838"
            variant="flat"
            :loading="creating"
            @click="createCustomer"
          >
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { CompanyCustomer, CreateCompanyCustomerDTO } from "../types/company.type";
import {
  createCompanyCustomer,
  deleteCompanyCustomer,
  getCompanyCustomers,
  updateCompanyCustomer,
} from "../services/api.service";
import { useCompanyStore } from "../stores/company.store";
import { humanizeDate } from "../helpers/utils.helper";
import { extractErrorMessage } from "../helpers/error.helper";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";

const loading = ref(false);
const { t } = useI18n();
const { mobile } = useDisplay();

// The table's own column-hiding used to key off Vuetify's `mobile` flag
// (default breakpoint 1280px) while the toolbar CSS below reflows at 768px
// (`bp.tablet`) - that mismatch left a dead zone where columns were already
// collapsed but the toolbar still assumed the wide desktop layout. Tracking
// the same 768px threshold here keeps both in sync.
const isNarrow = ref(window.matchMedia("(max-width: 768px)").matches);
const narrowQuery = window.matchMedia("(max-width: 768px)");
const onNarrowChange = (e: MediaQueryListEvent) => {
  isNarrow.value = e.matches;
};
onMounted(() => narrowQuery.addEventListener("change", onNarrowChange));
onUnmounted(() => narrowQuery.removeEventListener("change", onNarrowChange));

// Below isNarrow (see above), the plain contact-list in the template takes
// over instead - these headers only ever apply to the desktop table.
const headers = computed(() => [
  { title: t("fio"), value: "name", align: "start" as const },
  { title: t("email"), value: "email" },
  { title: t("phone_number"), value: "phone_number" },
  { title: t("visits_count"), value: "visit_count" },
  {
    title: t("last_visit_date"),
    key: "last_visit",
    value: "last_visit",
  },
  {
    title: t("last_service"),
    value: "last_service_name",
  },
]);

const companyStore = useCompanyStore();

const customers = ref<CompanyCustomer[]>([]);

const search = ref("");
const itemsPerPage = ref(10);
const page = ref(1);
const totalItems = ref(0);

const perPageChoices = ref([
  { title: "10", value: 1 },
  { title: "20", value: 20 },
  { title: "50", value: 50 },
]);

const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value) || 1);

// The mobile list has no v-data-table to filter its rows for it, so
// `search` (shared with the toolbar field above) is applied by hand here -
// same fields (name/phone) the desktop table's own built-in search covers.
const filteredCustomers = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return customers.value;

  return customers.value.filter(
    (customer) =>
      customer.name.toLowerCase().includes(query) ||
      customer.phone_number?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query)
  );
});

// The single source of truth for what's on screen: re-fetches the
// current page from the server rather than patching `customers.value` by
// hand, so create/edit/delete can never drift from whatever the backend
// actually returns.
const loadCustomers = async () => {
  if (!companyStore.currentCompany) return;

  loading.value = true;
  try {
    const response = await getCompanyCustomers(
      companyStore.currentCompany.id,
      itemsPerPage.value,
      page.value
    );
    customers.value = response.customers;
    totalItems.value = response.pages.itemsLength;
  } finally {
    loading.value = false;
  }
};

onMounted(loadCustomers);

const dialog = ref(false);
const saving = ref(false);
const saveError = ref("");
const editedCustomer = ref<CompanyCustomer>({} as CompanyCustomer);
const confirmingDelete = ref(false);
const deleting = ref(false);
const deleteError = ref("");

const editItem = (item: CompanyCustomer) => {
  editedCustomer.value = { ...item };
  saveError.value = "";
  confirmingDelete.value = false;
  deleteError.value = "";
  dialog.value = true;
};

const saveCustomer = async () => {
  if (!companyStore.currentCompany || !editedCustomer.value.id) return;

  saving.value = true;
  saveError.value = "";
  try {
    await updateCompanyCustomer(
      companyStore.currentCompany.id!,
      editedCustomer.value.id,
      {
        name: editedCustomer.value.name,
        phone_number: editedCustomer.value.phone_number,
        email: editedCustomer.value.email,
        address: editedCustomer.value.address,
      }
    );

    await loadCustomers();
    dialog.value = false;
  } catch (error) {
    saveError.value = extractErrorMessage(error, t("could_not_save"));
  }
  saving.value = false;
};

// Only removes this company's own record of them - their reservations
// themselves and a linked User account are never touched. See
// deleteCompanyCustomer.
const deleteCustomer = async () => {
  if (!companyStore.currentCompany || !editedCustomer.value.id) return;

  deleting.value = true;
  deleteError.value = "";
  try {
    await deleteCompanyCustomer(companyStore.currentCompany.id!, editedCustomer.value.id);
    await loadCustomers();
    dialog.value = false;
  } catch (error) {
    deleteError.value = extractErrorMessage(error, t("could_not_delete"));
  }
  deleting.value = false;
};

const createDialog = ref(false);
const creating = ref(false);
const createError = ref("");
const newCustomer = ref<CreateCompanyCustomerDTO>({ name: "" });

const openCreateDialog = () => {
  newCustomer.value = { name: "" };
  createError.value = "";
  createDialog.value = true;
};

const createCustomer = async () => {
  if (!companyStore.currentCompany || !newCustomer.value.name.trim()) {
    createError.value = t("customer_required");
    return;
  }

  creating.value = true;
  createError.value = "";
  try {
    await createCompanyCustomer(companyStore.currentCompany.id!, newCustomer.value);
    await loadCustomers();
    createDialog.value = false;
  } catch (error) {
    createError.value = extractErrorMessage(error, t("could_not_save"));
  }
  creating.value = false;
};

const updatedOptions = async (options: {
  page: number;
  itemsPerPage: number | undefined;
}) => {
  page.value = options.page;
  if (options.itemsPerPage) itemsPerPage.value = options.itemsPerPage;
  await loadCustomers();
};

// The mobile list's own v-pagination, standing in for the desktop table's
// built-in page controls (which it doesn't have, having no v-data-table).
const goToPage = async (newPage: number) => {
  page.value = newPage;
  await loadCustomers();
};
</script>
<style lang="scss">
@use "../styles/_breakpoints" as bp;

.customers {
  height: calc(95vh - 126px);
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  column-gap: 20px;

  @include bp.tablet {
    padding: 0 12px;
    height: calc(95vh - 96px);
  }

  &__content {
    width: 100%;
    border-radius: 20px;
    background: #fff;
    padding: 10px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    padding: 40px 16px;
    color: #696e7c;
    text-align: center;

    p {
      margin: 0;
      font-size: 14px;
    }
  }

  // Shared by both the desktop table and the mobile contact-list below it.
  &__toolbar {
    // Vuetify wraps a v-toolbar's content in a non-wrapping flex row by
    // default - the search field would otherwise force it to overflow on
    // narrow screens instead of dropping to its own line.
    flex-wrap: wrap;
    height: auto !important;

    @include bp.tablet {
      padding-block: 8px;
    }

    .v-toolbar__content {
      height: auto !important;
      flex-wrap: wrap;
      row-gap: 8px;
    }

    // The title has Vuetify's default nowrap + ellipsis, so with room only
    // for shrinking (not wrapping) it collapses to a couple of characters
    // before the search field. Forcing it onto its own full-width row lets
    // it show completely instead.
    .v-toolbar-title {
      @include bp.tablet {
        flex: 1 1 100%;
        order: -1;
      }
    }

    .v-divider--vertical {
      @include bp.tablet {
        display: none;
      }
    }

    .v-text-field {
      @include bp.tablet {
        min-width: 0;
        flex: 1 1 160px;
      }
    }
  }

  &__table {
    border-radius: 20px;
    height: 100%;

    &-bottom {
      align-items: center;
    }
  }

  &__list {
    background: transparent;
  }

  &__list-visits {
    color: #696e7c;
    font-size: 13px;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }
}
</style>
